import type { SVGProps } from "react";

/**
 * Husets ikonsaet. Tegnet i haanden, fordi der bevidst ikke er nogen ikonpakke
 * i projektet (se PRODUCT.md, "Tekniske rammer").
 *
 * Alle ikoner deler praecis det idiom TopBar allerede brugte til sol/maane:
 * 24-viewBox, ingen fyld, stroke i currentColor, 1.7 i tykkelse, runde ender.
 * Det betyder at farven altid kommer fra en token via `text-*`, og at et ikon
 * kan skaleres med `h-*`/`w-*` uden at stregen falder fra hinanden.
 *
 * De erstatter de emoji og unicode-tegn der stod som ikoner foer. Emoji
 * renderes af styresystemet i fuld farve og kan hverken tage tema eller vaegt,
 * hvilket traekker direkte imod "energi uden science fiction".
 */

const svgBase: SVGProps<SVGSVGElement> = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  focusable: "false",
};

interface IconProps {
  className?: string;
}

/** Vaeghaengt ladeboks med kabel. Trin 2: "jeg har en hjemmelader". */
export const IconLadeboks = (p: IconProps) => (
  <svg {...svgBase} {...p}>
    <rect x="5" y="3" width="14" height="12" rx="2.5" />
    <path d="M12.9 6.3 10.3 10h3.4l-2.6 3.7" />
    <path d="M12 15v3a3 3 0 0 0 3 3h1" />
  </svg>
);

/** Samme boks, stiplet og med et plus: den er der ikke endnu. Trin 2:
 *  "skal have installation". Samme motiv som IconLadeboks med vilje, saa de to
 *  valg laeses som to tilstande af det samme og ikke som to forskellige ting. */
export const IconInstallation = (p: IconProps) => (
  <svg {...svgBase} {...p}>
    <rect x="5" y="3" width="14" height="12" rx="2.5" strokeDasharray="3 2.6" />
    <path d="M12 6.6v4.8M9.6 9h4.8" />
    <path d="M12 15v3a3 3 0 0 0 3 3h1" />
  </svg>
);

/** Paere. Hint-bokse. Holdt paa tre elementer: ved 18-20 px smelter en
 *  detaljeret paere sammen til en klat. */
export const IconHint = (p: IconProps) => (
  <svg {...svgBase} {...p}>
    <circle cx="12" cy="8.8" r="5.8" />
    <path d="M9.4 17.6h5.2" />
    <path d="M10.6 20.8h2.8" />
  </svg>
);

/** Bil. Trin 3: opladning ude. */
export const IconBil = (p: IconProps) => (
  <svg {...svgBase} {...p}>
    <path d="M5.4 8.6 7 5.5a2 2 0 0 1 1.8-1.1h6.4a2 2 0 0 1 1.8 1.1l1.6 3.1" />
    <rect x="3.2" y="8.6" width="17.6" height="6" rx="2" />
    <path d="M6.6 11.6h1.2M16.2 11.6h1.2" />
    <circle cx="7.6" cy="17.2" r="1.9" />
    <circle cx="16.4" cy="17.2" r="1.9" />
  </svg>
);

/** Lyn. Tag for "offentlig opladning inkluderet". */
export const IconLyn = (p: IconProps) => (
  <svg {...svgBase} {...p}>
    <path d="M13 2.6 3.6 14h8.4l-.9 7.4L20.4 10H12l1-7.4z" />
  </svg>
);

/** Chevron ned. Udfold, fold sammen, vis flere. Roteres af kaldstedet. */
export const IconChevron = (p: IconProps) => (
  <svg {...svgBase} {...p}>
    <path d="M6.4 9.6 12 15.2l5.6-5.6" />
  </svg>
);

/** Flag. Rapportér fejl. */
export const IconFlag = (p: IconProps) => (
  <svg {...svgBase} {...p}>
    <path d="M5.6 21V3.8" />
    <path d="M5.6 4.8h11.6l-2.3 3.5 2.3 3.5H5.6" />
  </svg>
);

/** Stjerne. "Bedst for dig" og resultat-trinnet i wizarden. */
export const IconStjerne = (p: IconProps) => (
  <svg {...svgBase} {...p}>
    <path d="m12 3.1 2.75 5.57 6.15.9-4.45 4.33 1.05 6.12L12 17.13l-5.5 2.89 1.05-6.12L3.1 9.57l6.15-.9z" />
  </svg>
);

/** Kryds. Luk dialog. */
export const IconLuk = (p: IconProps) => (
  <svg {...svgBase} {...p}>
    <path d="M6.4 6.4 17.6 17.6M17.6 6.4 6.4 17.6" />
  </svg>
);

/** Pil ned i bakke. Gem som PDF. */
export const IconDownload = (p: IconProps) => (
  <svg {...svgBase} {...p}>
    <path d="M12 3.4v11.2" />
    <path d="M7.8 10.4 12 14.6l4.2-4.2" />
    <path d="M4.6 17.4v1.4a1.8 1.8 0 0 0 1.8 1.8h11.2a1.8 1.8 0 0 0 1.8-1.8v-1.4" />
  </svg>
);

/** Cirkelpil. Start forfra. */
export const IconGenstart = (p: IconProps) => (
  <svg {...svgBase} {...p}>
    <path d="M20.4 12a8.4 8.4 0 1 1-2.46-5.94" />
    <path d="M20.4 3.8v5.2h-5.2" />
  </svg>
);

/** Plus. Tilfoej egen aftale. */
export const IconPlus = (p: IconProps) => (
  <svg {...svgBase} {...p}>
    <path d="M12 5.4v13.2M5.4 12h13.2" />
  </svg>
);

/** Pil ned. Besparelse i forhold til den dyreste aftale. */
export const IconPilNed = (p: IconProps) => (
  <svg {...svgBase} {...p}>
    <path d="M12 4.8v14.4" />
    <path d="M6.4 13.6 12 19.2l5.6-5.6" />
  </svg>
);

/** Advarselstrekant. Aftalen kraever at man selv har en ladeboks. */
export const IconAdvarsel = (p: IconProps) => (
  <svg {...svgBase} {...p}>
    <path d="M10.7 4.5 2.6 18.4a1.5 1.5 0 0 0 1.3 2.2h16.2a1.5 1.5 0 0 0 1.3-2.2L13.3 4.5a1.5 1.5 0 0 0-2.6 0Z" />
    <path d="M12 9.6v4" />
    <path d="M12 16.8h.01" />
  </svg>
);
